/**
 * Defines a Vue.js directive, v-md, that allows you to parse and render Markdown
 * text directly in your Vue templates. The directive uses the 'micromark' library for parsing
 * Markdown, and the parsing behavior can be customized using the 'MarkdownExtentions' configuration object.
 *
 * The 'v-md' directive can be used in any Vue template like this:
 *
 * <div v-md="markdownText"></div>
 *
 * Where 'markdownText' is a variable in your Vue component's data that contains the Markdown
 * text to be rendered. The directive will automatically parse the Markdown text and render
 * the resulting HTML inside the div.
 */
import type { App, DirectiveBinding } from "vue";
import { micromark } from "micromark";
import { MarkdownExtentions } from "./MarkdownExtentions";

export default class Markdown {
	// Called by Vue.use() to enable the v-md directive.
	public static install(app: App): void {
		app.directive("md", {
			// eslint-disable-next-line @typescript-eslint/unbound-method
			beforeMount: Markdown.bind,
			// eslint-disable-next-line @typescript-eslint/unbound-method
			updated: Markdown.update,
			// eslint-disable-next-line @typescript-eslint/unbound-method
			unmounted: Markdown.unbind,
		});
	}

	protected static unbind(this: undefined, el: Element): void {
		el.innerHTML = "";
	}

	protected static bind(this: undefined, el: Element, binding: DirectiveBinding<string>): void {
		el.innerHTML = micromark(binding.value, undefined, { extensions: [MarkdownExtentions] });
	}

	protected static update(this: undefined, el: Element, binding: DirectiveBinding<string>): void {
		if (binding.value === binding.oldValue) {
			return;
		}

		Markdown.bind.call(this, el, binding);
	}
}
